import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CustomFieldApplication,
  GetCustomFieldApplicationResponse,
  GetCustomFieldApplicationsResponse,
  GetMembersCustomFieldApplicationsResponse,
  GetRolesCustomFieldApplicationsResponse,
  UpdateCustomFieldApplication,
  createCustomFieldApplication as universalCreateCustomFieldApplication,
  deleteCustomFieldApplication as universalDeleteCustomFieldApplication,
  getCustomFieldApplication as universalGetCustomFieldApplication,
  getCustomFieldApplications as universalGetCustomFieldApplications,
  getMembersCustomFieldApplications as universalGetMembersCustomFieldApplications,
  getRolesCustomFieldApplications as universalGetRolesCustomFieldApplications,
  updateCustomFieldApplication as universalUpdateCustomFieldApplication,
} from './members-v1-custom-field-application-custom-field-applications.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function createCustomFieldApplication(
  httpClient: HttpClient
): CreateCustomFieldApplicationSignature {
  return (
    application: NonNullablePaths<
      CustomFieldApplication,
      | `applications.items.${number}.entityId`
      | `customFieldId`
      | `exclusions.items.${number}.entityId`,
      5
    >
  ) =>
    universalCreateCustomFieldApplication(
      application,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCustomFieldApplicationSignature {
  /**
   * Creates a custom field application.
   * @param - Custom field application details.
   * @returns The created custom field application.
   */
  (
    application: NonNullablePaths<
      CustomFieldApplication,
      | `applications.items.${number}.entityId`
      | `customFieldId`
      | `exclusions.items.${number}.entityId`,
      5
    >
  ): Promise<
    NonNullablePaths<
      CustomFieldApplication,
      | `applications.items`
      | `applications.items.${number}.applicationType`
      | `exclusions.items`
      | `exclusions.items.${number}.exclusionType`,
      5
    >
  >;
}

export function deleteCustomFieldApplication(
  httpClient: HttpClient
): DeleteCustomFieldApplicationSignature {
  return (customFieldId: string) =>
    universalDeleteCustomFieldApplication(
      customFieldId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCustomFieldApplicationSignature {
  /**
   * Deletes a custom field application.
   *
   * When you delete the application, the field is automatically applied to all members.
   * @param - ID of the custom field with an application to delete.
   */
  (customFieldId: string): Promise<void>;
}

export function updateCustomFieldApplication(
  httpClient: HttpClient
): UpdateCustomFieldApplicationSignature {
  return (
    customFieldId: string,
    application: NonNullablePaths<
      UpdateCustomFieldApplication,
      | `applications.items.${number}.entityId`
      | `exclusions.items.${number}.entityId`
      | `revision`,
      5
    >
  ) =>
    universalUpdateCustomFieldApplication(
      customFieldId,
      application,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCustomFieldApplicationSignature {
  /**
   * Updates a custom field application.
   *
   * Partial updates are not supported. Pass the whole `application` object in the request.
   * @param - Custom field ID.
   * @returns Updated custom field application.
   */
  (
    customFieldId: string,
    application: NonNullablePaths<
      UpdateCustomFieldApplication,
      | `applications.items.${number}.entityId`
      | `exclusions.items.${number}.entityId`
      | `revision`,
      5
    >
  ): Promise<
    NonNullablePaths<
      CustomFieldApplication,
      | `applications.items`
      | `applications.items.${number}.applicationType`
      | `exclusions.items`
      | `exclusions.items.${number}.exclusionType`,
      5
    >
  >;
}

export function getCustomFieldApplication(
  httpClient: HttpClient
): GetCustomFieldApplicationSignature {
  return (customFieldId: string) =>
    universalGetCustomFieldApplication(
      customFieldId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCustomFieldApplicationSignature {
  /**
   * Retrieves a custom field application.
   * @param - ID of the custom field with an application to retrieve.
   */
  (customFieldId: string): Promise<
    NonNullablePaths<
      GetCustomFieldApplicationResponse,
      | `application.applications.items`
      | `application.applications.items.${number}.applicationType`
      | `application.exclusions.items`
      | `application.exclusions.items.${number}.exclusionType`,
      6
    >
  >;
}

export function getCustomFieldApplications(
  httpClient: HttpClient
): GetCustomFieldApplicationsSignature {
  return (customFieldIds: string[]) =>
    universalGetCustomFieldApplications(
      customFieldIds,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCustomFieldApplicationsSignature {
  /**
   * Retrieves a list of custom field applications.
   * @param - List of IDs of the custom fields with applications to retrieve.
   */
  (customFieldIds: string[]): Promise<
    NonNullablePaths<GetCustomFieldApplicationsResponse, `applications`, 2>
  >;
}

export function getMembersCustomFieldApplications(
  httpClient: HttpClient
): GetMembersCustomFieldApplicationsSignature {
  return (memberIds: string[]) =>
    universalGetMembersCustomFieldApplications(
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMembersCustomFieldApplicationsSignature {
  /**
   * Retrieves a list of fields that are applied to specified members.
   * @param - IDs of members with custom field applications to retrieve.
   */
  (memberIds: string[]): Promise<
    NonNullablePaths<GetMembersCustomFieldApplicationsResponse, `results`, 2>
  >;
}

export function getRolesCustomFieldApplications(
  httpClient: HttpClient
): GetRolesCustomFieldApplicationsSignature {
  return (roleIds: string[]) =>
    universalGetRolesCustomFieldApplications(
      roleIds,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRolesCustomFieldApplicationsSignature {
  /**
   * Retrieves a list of fields that are applied to specified roles.
   * @param - IDs of roles with custom field applications to retrieve.
   */
  (roleIds: string[]): Promise<
    NonNullablePaths<GetRolesCustomFieldApplicationsResponse, `results`, 2>
  >;
}

export {
  AccountInfo,
  ActionEvent,
  ApplicationsWrapper,
  AppliesTo,
  AppliesToEnumAppliesTo,
  CreateCustomFieldApplicationRequest,
  CreateCustomFieldApplicationResponse,
  CustomField,
  CustomFieldApplication,
  DeleteCustomFieldApplicationRequest,
  DeleteCustomFieldApplicationResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Exclusion,
  ExclusionType,
  ExclusionsWrapper,
  FieldApplication,
  FieldTypeType,
  GetCustomFieldApplicationRequest,
  GetCustomFieldApplicationResponse,
  GetCustomFieldApplicationsRequest,
  GetCustomFieldApplicationsResponse,
  GetMembersCustomFieldApplicationsRequest,
  GetMembersCustomFieldApplicationsResponse,
  GetRolesCustomFieldApplicationsRequest,
  GetRolesCustomFieldApplicationsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MemberCustomFieldApplication,
  MessageEnvelope,
  Origin,
  Privacy,
  RestoreInfo,
  RoleCustomFieldApplication,
  Section,
  SocialTypeType,
  Type,
  UpdateCustomFieldApplication,
  UpdateCustomFieldApplicationRequest,
  UpdateCustomFieldApplicationResponse,
  WebhookIdentityType,
} from './members-v1-custom-field-application-custom-field-applications.universal.js';
